import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Instagram, Music2, Youtube, Facebook, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- Embed-script loaders ----------
// Each platform's official embed.js must be on the page exactly once. We then
// (re-)trigger their "process" calls whenever new posts mount.
function loadScript(src, key) {
  if (typeof window === "undefined") return null;
  if (document.querySelector(`script[data-embed="${key}"]`)) return null;
  const s = document.createElement("script");
  s.src = src; s.async = true; s.dataset.embed = key;
  document.body.appendChild(s);
  return s;
}

function reprocessEmbeds() {
  // Instagram
  if (window.instgrm?.Embeds?.process) {
    try { window.instgrm.Embeds.process(); } catch { /* ignore */ }
  }
  // TikTok script auto-processes any blockquote on insertion, but reload helps
  // when the script is already cached.
  // YouTube/Facebook iframes don't need processing.
}

// ---------- Per-platform embed components ----------
function InstagramEmbed({ url }) {
  return (
    <blockquote className="instagram-media bg-white rounded-lg overflow-hidden"
                data-instgrm-permalink={url}
                data-instgrm-version="14"
                style={{ background: "#FFF", border: 0, margin: 0, padding: 0, minWidth: "326px", width: "100%" }}>
      <a href={url} target="_blank" rel="noreferrer" className="text-xs">View on Instagram</a>
    </blockquote>
  );
}

function TikTokEmbed({ url }) {
  // Extract the video id from the URL — TikTok needs cite + data-video-id
  const m = url.match(/\/video\/(\d+)/);
  const vid = m ? m[1] : undefined;
  return (
    <blockquote className="tiktok-embed"
                cite={url}
                data-video-id={vid}
                style={{ maxWidth: "605px", minWidth: "325px", margin: 0 }}>
      <section><a href={url} target="_blank" rel="noreferrer" className="text-xs">View on TikTok</a></section>
    </blockquote>
  );
}

function YouTubeEmbed({ url }) {
  // Extract id from a few common formats
  let id = null;
  const a = url.match(/[?&]v=([^&]+)/);
  const b = url.match(/youtu\.be\/([^?&]+)/);
  const c = url.match(/youtube\.com\/shorts\/([^?&]+)/);
  if (a) id = a[1]; else if (b) id = b[1]; else if (c) id = c[1];
  if (!id) return null;
  return (
    <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
      <iframe src={`https://www.youtube.com/embed/${id}`}
              title="YouTube video"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full border-0" />
    </div>
  );
}

function FacebookEmbed({ url }) {
  const src = `https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(url)}&show_text=true&width=500`;
  return (
    <iframe src={src}
            title="Facebook post"
            loading="lazy"
            scrolling="no"
            frameBorder="0"
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            className="w-full bg-white rounded-lg"
            style={{ minHeight: 560, border: 0, overflow: "hidden" }} />
  );
}

function PostCard({ p }) {
  const platforms = {
    instagram: { label: "Instagram", Icon: Instagram, color: "text-pink-600", Embed: InstagramEmbed },
    tiktok:    { label: "TikTok",    Icon: Music2,    color: "text-slate-900", Embed: TikTokEmbed },
    youtube:   { label: "YouTube",   Icon: Youtube,   color: "text-red-600",  Embed: YouTubeEmbed },
    facebook:  { label: "Facebook",  Icon: Facebook,  color: "text-blue-700", Embed: FacebookEmbed },
  };
  const meta = platforms[p.kind];
  if (!meta) return null;
  const { Icon, Embed, label, color } = meta;
  return (
    <div className="bg-white border border-nh-border overflow-hidden flex flex-col hover:-translate-y-1 transition-transform duration-300">
      <div className="flex items-center justify-between px-4 py-3 border-b border-nh-border">
        <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-widest ${color}`}>
          <Icon size={14} /> {label}
        </div>
        <a href={p.url} target="_blank" rel="noreferrer" className="text-[11px] text-nh-slate hover:text-nh font-semibold">Open ↗</a>
      </div>
      <div className="p-3 bg-slate-50 flex-1">
        <Embed url={p.url} />
        {p.caption && <p className="text-xs text-nh-slate mt-3 leading-relaxed">{p.caption}</p>}
      </div>
    </div>
  );
}

export default function SocialFeed() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/social`)
      .then(r => setPosts(r.data || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!posts.length) return;
    const kinds = new Set(posts.map(p => p.kind));
    if (kinds.has("instagram")) loadScript("https://www.instagram.com/embed.js", "instagram");
    if (kinds.has("tiktok"))    loadScript("https://www.tiktok.com/embed.js", "tiktok");
    if (kinds.has("facebook"))  loadScript("https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v18.0", "facebook");
    const t = setTimeout(reprocessEmbeds, 600);
    return () => clearTimeout(t);
  }, [posts]);

  if (!loading && posts.length === 0) return null;

  return (
    <section id="social" className="bg-nh-surface py-24 md:py-32" ref={containerRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-12">
          <div className="md:col-span-8">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-nh mb-4">Live feed</div>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight text-nh-ink leading-[1.05]">From the socials</h2>
            <p className="text-sm text-nh-slate mt-3">Latest from Instagram, TikTok, YouTube &amp; Facebook.</p>
          </div>
          <a href="https://www.instagram.com/wilsoncontractors" target="_blank" rel="noreferrer"
             className="md:col-span-4 text-sm text-nh hover:text-nh-dark flex items-center md:justify-end gap-2 font-semibold">
            <Instagram size={15} /> @wilsoncontractors
          </a>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-nh-slate text-sm">
            <Loader2 className="animate-spin mr-2" size={16} /> Loading posts…
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {posts.map(p => <PostCard key={p.id} p={p} />)}
          </div>
        )}
      </div>
    </section>
  );
}
